import React, { useState } from "react"
import { faCalendar, faClose, faFilter, faMagnifyingGlass, faMoneyBillWave } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import InputField from "../../../components/InputField/InputField"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import GetSpace from "../../../utils/space"
import { EManualExternalOrderDeliveryType } from "../../../Objects/ManualExternalOrder/EManualExternalOrderDeliveryType"
import { EManualExternalOrderReconciliationStatus } from "../../../Objects/ManualExternalOrder/EManualExternalOrderReconciliationStatus"
import { EManualExternalOrderSourceType } from "../../../Objects/ManualExternalOrder/EManualExternalOrderSourceType"
import { EManualExternalOrderStatus } from "../../../Objects/ManualExternalOrder/EManualExternalOrderStatus"
import { ManualExternalOrderFilter } from "../../../Objects/ManualExternalOrder/ManualExternalOrderFilter"
import {
  getManualExternalOrderDeliveryLabel,
  getManualExternalOrderPaymentLabel,
  getManualExternalOrderReconciliationLabel,
  getManualExternalOrderSourceLabel,
  getManualExternalOrderStatusLabel,
  createManualExternalOrderDropdownOptions,
  getManualExternalOrderDropdownValue,
  manualExternalOrderDeliveryOptions,
  manualExternalOrderPaymentOptions,
  manualExternalOrderReconciliationOptions,
  manualExternalOrderSourceOptions,
  manualExternalOrderStatusOptions,
} from "../../../Objects/ManualExternalOrder/manualExternalOrderHelpers"
import { EPaymentType } from "../../../Enum/EPaymentType"

interface ManualExternalOrderFiltersPopupProps {
  filter: ManualExternalOrderFilter
  onApply: (filter: ManualExternalOrderFilter) => void
  onClear: () => void
  onClose: () => void
}

export const ManualExternalOrderFiltersPopup: React.FC<ManualExternalOrderFiltersPopupProps> = ({
  filter,
  onApply,
  onClear,
  onClose,
}) => {
  const { t } = useTranslation("manualExternalOrder")

  const [draft, setDraft] = useState<ManualExternalOrderFilter>({ ...filter })

  const patch = (data: Partial<ManualExternalOrderFilter>) => setDraft((current) => ({ ...current, ...data }))

  const handleApply = () => {
    onApply({ ...draft })

    onClose()
  }

  const handleClear = () => {
    onClear()

    onClose()
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert2}>
            <TitlePageResponsiveIconSmall
              title={t("filters.popup_title")}
              icon={faFilter}
              iconColor={THEME.colors.iconBGColor}
              children={
                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderFiltersPopup.button.onClose"
                />
              }
            />

            <div className="grid grid-cols-1 p-3 gap-3 md:grid-cols-2">
              <InputField
                icon={faCalendar}
                type="date"
                value={draft.dateStart ?? ""}
                placeholder={t("filters.date_start")}
                onChange={(event) => patch({ dateStart: event.target.value })}
                descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderFiltersPopup.input.dateStart"
              />

              <InputField
                icon={faCalendar}
                type="date"
                value={draft.dateEnd ?? ""}
                placeholder={t("filters.date_end")}
                onChange={(event) => patch({ dateEnd: event.target.value })}
                descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderFiltersPopup.input.dateEnd"
              />

              <InputField
                icon={faMoneyBillWave}
                type="number"
                value={draft.minAmount ?? ""}
                placeholder={t("filters.min_amount")}
                onChange={(event) => patch({ minAmount: event.target.value })}
                descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderFiltersPopup.input.minAmount"
              />

              <InputField
                icon={faMoneyBillWave}
                type="number"
                value={draft.maxAmount ?? ""}
                placeholder={t("filters.max_amount")}
                onChange={(event) => patch({ maxAmount: event.target.value })}
                descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderFiltersPopup.input.maxAmount"
              />

              <div className="md:col-span-2">
                <InputField
                  icon={faMagnifyingGlass}
                  type="text"
                  value={draft.search ?? ""}
                  placeholder={t("filters.search")}
                  onChange={(event) => patch({ search: event.target.value })}
                  descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderFiltersPopup.input.search"
                />
              </div>

              <GenericDropDown
                initialState={getManualExternalOrderDropdownValue(draft.sourceType)}
                data={createManualExternalOrderDropdownOptions(
                  manualExternalOrderSourceOptions,
                  getManualExternalOrderSourceLabel,
                  t("filters.all_sources"),
                )}
                handlerFuncion={(event) =>
                  patch({
                    sourceType:
                      event.target.value === ""
                        ? ""
                        : (event.target.value.toNumber() as EManualExternalOrderSourceType),
                  })
                }
                descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderFiltersPopup.select.select"
              />

              <GenericDropDown
                initialState={getManualExternalOrderDropdownValue(draft.status)}
                data={createManualExternalOrderDropdownOptions(
                  manualExternalOrderStatusOptions,
                  getManualExternalOrderStatusLabel,
                  t("filters.all_status"),
                )}
                handlerFuncion={(event) =>
                  patch({
                    status:
                      event.target.value === "" ? "" : (event.target.value.toNumber() as EManualExternalOrderStatus),
                  })
                }
                descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderFiltersPopup.select.select2"
              />

              <GenericDropDown
                initialState={getManualExternalOrderDropdownValue(draft.reconciliationStatus)}
                data={createManualExternalOrderDropdownOptions(
                  manualExternalOrderReconciliationOptions,
                  getManualExternalOrderReconciliationLabel,
                  t("filters.all_reconciliation"),
                )}
                handlerFuncion={(event) =>
                  patch({
                    reconciliationStatus:
                      event.target.value === ""
                        ? ""
                        : (event.target.value.toNumber() as EManualExternalOrderReconciliationStatus),
                  })
                }
                descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderFiltersPopup.select.select3"
              />

              <GenericDropDown
                initialState={getManualExternalOrderDropdownValue(draft.paymentMethod)}
                data={createManualExternalOrderDropdownOptions(
                  manualExternalOrderPaymentOptions,
                  getManualExternalOrderPaymentLabel,
                  t("filters.all_payments"),
                )}
                handlerFuncion={(event) =>
                  patch({
                    paymentMethod: event.target.value === "" ? "" : (event.target.value.toNumber() as EPaymentType),
                  })
                }
                descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderFiltersPopup.select.select4"
              />

              <GenericDropDown
                initialState={getManualExternalOrderDropdownValue(draft.deliveryType)}
                data={createManualExternalOrderDropdownOptions(
                  manualExternalOrderDeliveryOptions,
                  getManualExternalOrderDeliveryLabel,
                  t("filters.all_delivery"),
                )}
                handlerFuncion={(event) =>
                  patch({
                    deliveryType:
                      event.target.value === ""
                        ? ""
                        : (event.target.value.toNumber() as EManualExternalOrderDeliveryType),
                  })
                }
                descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderFiltersPopup.select.select5"
              />
            </div>

            <div className={defaultStyle.divisionBar}></div>

            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button
                label={t("actions.clear_filters")}
                type="secondary"
                onClick={handleClear}
                descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderFiltersPopup.button.clearFilters"
              />

              <Button
                label={t("actions.filter")}
                onClick={handleApply}
                descriptionKey="interactionDescriptions:pages.fluxo.manualExternalOrders.manualExternalOrderFiltersPopup.button.filter"
              />
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
